import { memo } from "react";
import { motion } from "framer-motion";
import type { Variants } from "framer-motion";

interface AnimatedLoaderProps {
  /** Callback disparado assim que a animação de preenchimento completa */
  onComplete?: () => void;
  /** Classe CSS customizada para sobreposição/layout */
  className?: string;
}

const PATHS_CONFIG = [
  {
    id: "white-path",
    d: "M2.57 10.23c.15-.09.21-.15.35-.24.23-.16.23-.13.27-.18.15-.17.12-.43.11-.51-.07-.73 0-5.96 0-6.03 0-.32-.09-.6-.37-.82h1.62c-.29.22-.36.51-.36.84v5.96c0 .16 0 .24.22.22.92-.09 1.75.22 2.57.58.88.38 1.72.85 2.61 1.19 1.26.49 2.53.56 3.82.24.58-.14 1.09-.47 1.6-.79.06-.04.12-.11.21-.1.04.07-.03.1-.06.13-1.21 1.21-2.67 1.64-4.35 1.49-1.39-.12-2.62-.66-3.82-1.32-.67-.37-1.33-.73-2.09-.89-.84-.17-2.12.2-2.21.23 0 0-.04 0-.09.03-.07.02-.12.03-.15.04.03-.02.08-.04.13-.08Z",
    color: "#FFFFFF",
  },
  {
    id: "turquoise-main",
    d: "M1.2.4s.98-.48 2.04-.3c.36.06.62.18.77.25.69.33 1.22.86 1.66 1.48.77 1.05 1.36 2.2 1.95 3.36.24.49.52.97.79 1.45.04.07.05.15.14.19.67-1.78 1.34-3.55 1.99-5.33.07-.18.15-.22.32-.21.33.01.66 0 1.03 0-.3.28-.39.59-.38.97.01 1.9 0 3.8 0 5.7 0 .32.09.58.38.8h-1.62c.35-.26.35-.63.35-1.01V2.26c-.08 0-.08.08-.09.12-.57 1.53-1.14 3.07-1.72 4.61-.05.14-.04.24.04.37.38.61.81 1.18 1.33 1.68.54.52 1.12.87 1.75 1.03.88.23 2.01-.02 2.16-.05.11-.02.33-.07.34-.05 0 .03-.71.25-1.31.43-.37.1-1.04.23-1.83.05-.25-.06-.86-.23-1.69-.89-.86-.69-1.46-1.6-2.01-2.54-.68-1.16-1.2-2.4-1.85-3.58-.45-.82-.91-1.64-1.59-2.3C3.56.56 3.08.39 2.7.31 2.14.2 1.66.29 1.38.37c-.13.02-.16.02-.16.01V.4Z",
    color: "#30C0D1",
    isPrimary: true,
  },
  {
    id: "turquoise-top-right",
    d: "M13.76 9.82c-.65 0-1.18-.53-1.18-1.18h2.34c0 .66-.52 1.18-1.17 1.18Z",
    color: "#30C0D1",
  },
  {
    id: "turquoise-bottom-left",
    d: "M1.21 1.88C.56 1.88.03 1.35.03.7h2.34c0 .66-.52 1.18-1.17 1.18Z",
    color: "#30C0D1",
  },
];

const pathVariants: Variants = {
  hidden: {
    pathLength: 0,
    fillOpacity: 0,
  },
  visible: (customColor: string) => ({
    pathLength: 1,
    fillOpacity: [0, 0, 1],
    fill: customColor,
    transition: {
      pathLength: {
        duration: 1.8,
        ease: [0.42, 0, 0.58, 1],
      },
      fillOpacity: {
        duration: 2.4,
        times: [0, 0.7, 1],
        ease: "easeInOut",
      },
    },
  }),
};

export const AnimatedLoader = memo(
  ({ onComplete, className = "min-h-screen bg-linear-to-b from-[#00363d] to-[#005c66]" }: AnimatedLoaderProps) => {
    return (
      <div role="status" aria-label="Carregando..." className={`flex w-full items-center justify-center p-4 ${className}`}>
        <motion.svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 15.29 12.31"
          className="-mt-14 h-auto w-28 drop-shadow-sm sm:mt-0 sm:w-36"
          style={{ willChange: "transform" }}
        >
          {PATHS_CONFIG.map(({ id, d, color, isPrimary }) => (
            <motion.path
              key={id}
              d={d}
              stroke={color}
              strokeWidth={0.08}
              variants={pathVariants}
              custom={color}
              initial="hidden"
              animate="visible"
              onAnimationComplete={isPrimary ? onComplete : undefined}
            />
          ))}
        </motion.svg>
      </div>
    );
  },
);

AnimatedLoader.displayName = "AnimatedLoader";
